import { type Control, useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import DeleteLine from '@/assets/icons/delete_line.svg?component';
import Alert from '@/components/atoms/Alert';
import { Button } from '@/components/atoms/Buttons';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import type { PenRef, ProviderRow } from '@/domain/providerConfig';
import { errorKeyOf } from '@/routes/shared/ProviderConfig/ProviderSchemaField';

import type { ProviderFormValues } from '../form/formModel';

import { PenActiveSwitch } from './PenActiveSwitch';
import { StatusPill } from './StatusPill';

import rowStyles from './penRow.module.scss';
import styles from './sections.module.scss';

export interface PenRowHeaderProps {
    id: string;
    pen: PenRef;
    row: ProviderRow | null;
    base: `pens.${string}`;
    control: Control<ProviderFormValues>;
}

/**
 * A pen's name and state, and what can be done to it: connect, undo, the Active switch,
 * remove. A pen with stored data can only be deactivated: removing its row would leave the
 * data without the mapping that explains it, and the server refuses it (ROW_HAS_DATA).
 */
function PenRowHeader({ id, pen, row, base, control }: PenRowHeaderProps) {
    const { t } = useTranslation();
    const { field: connected } = useController({ control, name: `${base}.connected` });
    const { field: remove, fieldState: removeState } = useController({
        control,
        name: `${base}.remove`,
    });
    const editing = connected.value && !remove.value;
    return (
        <>
            <div className={rowStyles.penHeader}>
                <span className={styles.penName}>
                    {pen.name}
                    {row?.description && (
                        <span className={rowStyles.penDescription}> · {row.description}</span>
                    )}
                </span>
                {!connected.value && (
                    <>
                        <StatusPill tone="neutral" label={t('providerConfig.pens.notConnected')} />
                        <Button variant="secondary" onClick={() => connected.onChange(true)}>
                            {t('providerConfig.pens.connect')}
                        </Button>
                    </>
                )}
                {connected.value && remove.value && (
                    <>
                        <StatusPill tone="bad" label={t('providerConfig.pens.willRemove')} />
                        <Button variant="secondary" onClick={() => remove.onChange(false)}>
                            {t('providerConfig.pens.undoRemove')}
                        </Button>
                    </>
                )}
                {editing && <PenActiveSwitch id={`${id}-active`} base={base} control={control} />}
                {editing && row === null && (
                    <Button variant="secondary" onClick={() => connected.onChange(false)}>
                        {t('providerConfig.pens.undoConnect')}
                    </Button>
                )}
                {editing && row !== null && !row.health.hasData && (
                    <Button
                        variant="danger"
                        icon={<SvgIcon icon={DeleteLine} color="currentColor" />}
                        onClick={() => remove.onChange(true)}>
                        {t('providerConfig.pens.remove')}
                    </Button>
                )}
            </div>
            {removeState.error && (
                <Alert tone="danger" size="small">
                    {t(errorKeyOf(removeState.error.message))}
                </Alert>
            )}
        </>
    );
}

export default PenRowHeader;
export { PenRowHeader };
